import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../primitives/button';
import { Card } from '../primitives/card';
import { cn } from '../lib/cn';

interface StepperProps {
  steps: string[];
  /** Zero-based index of the step being shown */
  current: number;
  /** "Step 2 of 4" */
  progressLabel?: (step: number, total: number) => string;
  className?: string;
}

/** Progress through a multi-step form. Names every step on wide screens, only the current one on phones. */
export function Stepper({
  steps,
  current,
  progressLabel = (step, total) => `Step ${step} of ${total}`,
  className,
}: StepperProps) {
  return (
    <nav aria-label={progressLabel(current + 1, steps.length)} className={className}>
      <p className="mb-2 text-sm font-medium text-muted-foreground sm:hidden">
        {progressLabel(current + 1, steps.length)}: <span className="text-foreground">{steps[current]}</span>
      </p>
      <ol className="flex items-center gap-2">
        {steps.map((step, index) => {
          const done = index < current;
          const active = index === current;
          return (
            <li
              key={step}
              aria-current={active ? 'step' : undefined}
              className="flex min-w-0 flex-1 items-center gap-2"
            >
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold tabular-nums',
                  done && 'border-secondary bg-secondary text-secondary-foreground',
                  active && 'border-primary bg-primary text-primary-foreground',
                  !done && !active && 'border-border bg-surface text-muted-foreground',
                )}
              >
                {done ? <Check className="size-4" aria-hidden /> : index + 1}
              </span>
              <span
                className={cn(
                  'hidden truncate text-sm sm:block',
                  active ? 'font-semibold' : 'text-muted-foreground',
                )}
              >
                {step}
              </span>
              {index < steps.length - 1 && <span aria-hidden className="h-px min-w-4 flex-1 bg-border" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

interface StepFormProps {
  steps: string[];
  current: number;
  title: string;
  description?: string;
  children: ReactNode;
  onBack?: () => void;
  onNext: () => void;
  /** Disables Next while the step is invalid or saving */
  nextDisabled?: boolean;
  loading?: boolean;
  labels?: {
    back?: string;
    next?: string;
    finish?: string;
    progress?: (step: number, total: number) => string;
  };
  className?: string;
}

/** Multi-step form: stepper, one step's fields, Back and Next (Finish on the last step). */
export function StepForm({
  steps,
  current,
  title,
  description,
  children,
  onBack,
  onNext,
  nextDisabled,
  loading,
  labels = {},
  className,
}: StepFormProps) {
  const { back = 'Back', next = 'Next', finish = 'Finish', progress } = labels;
  const last = current === steps.length - 1;
  return (
    <div className={cn('grid gap-4', className)}>
      <Stepper steps={steps} current={current} progressLabel={progress} />
      <Card className="grid gap-4 p-5 sm:p-6">
        <div className="grid gap-1">
          <h2 className="text-lg font-semibold">{title}</h2>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {children}
        <div className="flex justify-between gap-3 border-t border-border pt-5">
          {current > 0 && onBack ? (
            <Button variant="outline" onClick={onBack}>
              {back}
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={onNext} disabled={nextDisabled} loading={loading}>
            {last ? finish : next}
          </Button>
        </div>
      </Card>
    </div>
  );
}
